/**
 * The dates the activity views work in.
 *
 * The feeds take plain days, `YYYY-MM-DD`, inclusive at both ends, and read
 * them as UTC days. The range a view opens with is the last thirty days, so the
 * first request is bounded and the person can see in the two date boxes why
 * older entries are not there.
 *
 * Counted in UTC, as the feeds count. Counted in the reader's own calendar, a
 * US reader in the evening asked for a range ending at the UTC midnight already
 * behind them, and the evening's entries were not in it.
 */

/** A day as the feeds take it: the UTC day. */
export function toDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** The UTC today and the thirty days before it. */
export function lastThirtyDays(now: Date = new Date()): { from: string; to: string } {
  const from = new Date(now);
  from.setUTCDate(from.getUTCDate() - 30);
  return { from: toDay(from), to: toDay(now) };
}

/** When an entry happened: the day and the time, as the reader writes them. */
export function formatMoment(value: string): string {
  return new Date(value).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

/** How long ago, in words: "3 hours ago", "yesterday". */
export function relativeTime(value: string, now: Date = new Date()): string {
  const seconds = Math.round((new Date(value).getTime() - now.getTime()) / 1000);
  const steps: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['week', 604_800],
    ['day', 86_400],
    ['hour', 3_600],
    ['minute', 60],
  ];
  for (const [unit, size] of steps) {
    if (Math.abs(seconds) >= size)
      return RELATIVE.format(Math.round(seconds / size), unit);
  }
  return RELATIVE.format(seconds, 'second');
}
